import React, { useRef, useState } from "react";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import AddIcon from "@mui/icons-material/Add";
import ApiIcon from "@mui/icons-material/ApiOutlined";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import PlaceIcon from "@mui/icons-material/Place";
import useActions, { useAppDispatch, useAppSelector } from "../../app/hooks";
import { CloudUpload } from "@mui/icons-material";

import CreateBookmarkForm from "./CreateBookmarkForm";
import { selectEditingDash } from "../editor/dashEditorSlice";
import { selectNavLocation, describeLocation, NavLocation } from "../navigation/navigationSlice";
import { selectBookmarks, selectCurrentFolder, selectFolderName, selectFolderNameStack, selectFolderStack, 
  selectRepoId, selectReposMode, selectRoleId, selectRolesMode, setFolderName, setRepoId, setReposMode, setRolesMode } from "../library/librarySlice";

import {
  Workspace,
} from "../../types/User";

import {
  Bookmark,
} from "../../types/Bookmark";

/**
 * The create menu.
 *
 * @export
 * @param {{user: User | null, documentTemplates: DocumentTemplate[]}} props
 * @return {*}
 */
export function BookmarkMenu(props: {
  // user: User | null;
  // documentTemplates: DocumentTemplate[];
  workspaces: Workspace[] | undefined;
  workspaceId: number | undefined;
}) {
  const dispatch = useAppDispatch();
  const actions = useActions();
  const selectedDocumentTemplate = useAppSelector(selectEditingDash);
  const rolesMode = useAppSelector(selectRolesMode);
  const reposMode = useAppSelector(selectReposMode);
  const viewRoleId = useAppSelector(selectRoleId);
  const viewRepoId = useAppSelector(selectRepoId);
  const viewRepoPath = useAppSelector(selectFolderName);

  const currentFolder = useAppSelector(selectCurrentFolder);
  const currentFolderNameStack = useAppSelector(selectFolderNameStack);
  const currentFolderStack = useAppSelector(selectFolderStack);

  const bookmarks = useAppSelector(selectBookmarks);

  // The current location (graph view if open, else the library context) + the
  // inline name input for bookmarking it.
  const nav = useAppSelector(selectNavLocation);
  const [locName, setLocName] = useState("");
  const libLoc: NavLocation = { kind: "library", reposMode, rolesMode, repoId: viewRepoId, repoPath: viewRepoPath, roleId: viewRoleId };
  const currentLoc = nav.kind === "graph" ? nav : libLoc;
  const currentLabel = describeLocation(currentLoc);

  //console.log("A", viewRepoPath);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [anchorGitEl, setAnchorGitEl] = React.useState<null | HTMLElement>(null);

  const gotoBookmark = (bookmark:Bookmark) => {
    handleCloseMenu();
    
    dispatch(setReposMode(bookmark.view_repo_mode));
    dispatch(setRolesMode(bookmark.view_roles_mode));
    dispatch(setFolderName(bookmark.repo_path));
    dispatch(setRepoId(bookmark.repo_id));

    if (bookmark.repo_id > 0) {
      actions.fileList({id:bookmark.repo_id, folder:bookmark.repo_path});
      actions.gitInfo({id:bookmark.repo_id, folder:bookmark.repo_path, requestType: 'LOGS', count:10});
    }
  };

  // Create menu details
  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  // Bookmark the current location inline (saves the library context; graph-view
  // restore awaits a bookmark schema field for the graph location).
  const saveCurrentLocation = () => {
    actions.createBookmark({
      name: locName.trim() || currentLabel,
      repo_id: nav.kind === "graph" ? (nav.networkRepoId ?? 0) : viewRepoId,
      role_id: viewRoleId,
      view_repo_mode: reposMode,
      view_roles_mode: rolesMode,
      repo_path: viewRepoPath,
      folder_id: currentFolder,
      folder_stack: currentFolderStack.join(","),
      folder_names: currentFolderNameStack.join(","),
    });
    setLocName("");
    handleCloseMenu();
  };

  const [createBookmarkFormOpen, setCreateBookmarkFormOpen] = React.useState<{ open: boolean}>({ open: false });
  
  const handleClickCreateBookmart = () => {
    setAnchorEl(null);
    //setCreateDocumentFormOpen(true)
  };

  return (
    <Box sx={{ flexGrow: 0 }}>
      <Tooltip title="Bookmarks">
        <Button
          startIcon={<BookmarkIcon />}
          sx={{ color: "white" }}
          onClick={handleOpenMenu}
        >
          Bookmarks
        </Button>
      </Tooltip>
      <Menu
        sx={{ mt: "45px" }}
        id="menu-appbar"
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        keepMounted
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
      >
        <MenuItem key="createBookamark" onClick={() => setCreateBookmarkFormOpen({open: true})}>
          <ListItemIcon>
            <AddIcon></AddIcon>
          </ListItemIcon>
          <ListItemText>Create Bookmark</ListItemText>
        </MenuItem>
        { bookmarks.map(_ =>
          <MenuItem key={"bookmark_" + _.id} onClick={() => gotoBookmark(_)}>
          <ListItemText>{_.name}</ListItemText>
        </MenuItem>
        )}

        <Divider />
        <Box sx={{ px: 2, py: 1, width: 320 }} onKeyDown={(e) => e.stopPropagation()}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.25 }}>
            <PlaceIcon fontSize="small" color="action" />
            <Typography variant="caption" color="text.secondary">Current location</Typography>
          </Box>
          <Typography variant="body2" sx={{ wordBreak: "break-all", mb: 1 }}>{currentLabel}</Typography>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <TextField
              size="small"
              placeholder="Bookmark name…"
              value={locName}
              onChange={(e) => setLocName(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              sx={{ flex: 1 }}
            />
            <Button size="small" variant="contained" onClick={saveCurrentLocation}>Save</Button>
          </Box>
        </Box>
      </Menu>

      <CreateBookmarkForm
        open={createBookmarkFormOpen.open}
        parentCloseHandle={() => {
          setCreateBookmarkFormOpen({open: false});
          setAnchorEl(null);
        }}
        repo_id={viewRepoId}
        role_id={viewRoleId}
        view_repo_mode={reposMode}
        view_roles_mode={rolesMode}
        repo_path={viewRepoPath}

        folder_id={currentFolder}
        folder_stack={currentFolderStack.join(",")}
        folder_names={currentFolderNameStack.join(",")}
      />
    </Box>
  );
}
